import classnames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";

import { getDurationFromCSSvar } from "seneca-common/utils/functions";
import isValidFunction from "seneca-common/utils/functions/checks/isValidFunction";

import Fade from "features/common/components/FadeIn";
import OpenCloseStateContainer from "features/common/components/OpenCloseStateContainer";
import { CloseIcon } from "features/common/icons";
import { ModalPortal } from "features/common/modals/components/ModalNodeContext";
import {
  makeModalContainer,
  InjectedProps as MakeModalContainerProps
} from "features/common/modals/containers/Modal";

import styles from "./SideDrawer.module.scss";

const ANIMATION_DURATION = getDurationFromCSSvar(styles.animationduration);

type OpenCloseStateContainerProps = {
  open: boolean;
  setOpen: () => void;
  setClosed: () => void;
};

export type Props = {
  openFromRight?: boolean;
  children?: React.ReactNode;
  renderContents?: (arg0: () => void, arg1: () => void) => React.ReactNode;
  sideDrawerClassName?: string;
  renderButton: (onClick: () => void) => React.ReactNode;
};

type ComponentProps = MakeModalContainerProps &
  OpenCloseStateContainerProps &
  Props;

function SideDrawer({
  setModalClosed,
  setModalOpen,
  open,
  renderButton,
  children,
  renderContents,
  sideDrawerClassName,
  setOpen,
  setClosed,
  openFromRight
}: ComponentProps) {
  const [mountDrawer, setMountDrawer] = useState<boolean>(false);
  const unmountTimeout = useRef<NodeJS.Timeout>();
  const setOpenAfterMountedTimeout = useRef<NodeJS.Timeout>();

  const openDrawer = useCallback(() => {
    if (isValidFunction(setModalOpen)) {
      setModalOpen();
    }

    clearTimeout(unmountTimeout.current);
    setMountDrawer(true);
    setOpenAfterMountedTimeout.current = setTimeout(setOpen, 0);
  }, [setModalOpen, setOpen]);

  const closeDrawer = useCallback(() => {
    if (isValidFunction(setModalClosed)) {
      setModalClosed();
    }

    unmountTimeout.current = setTimeout(
      () => setMountDrawer(false),
      ANIMATION_DURATION
    );
    setClosed();
  }, [setModalClosed, setClosed]);

  useEffect(() => {
    return () => {
      clearTimeout(unmountTimeout.current);
      clearTimeout(setOpenAfterMountedTimeout.current);
    };
  }, [unmountTimeout, setOpenAfterMountedTimeout]);

  const onClick = open ? closeDrawer : openDrawer;

  return (
    <>
      {renderButton(onClick)}
      {mountDrawer && (
        <ModalPortal>
          <Fade show={open}>
            <div className={styles.overlay} onClick={closeDrawer} />
          </Fade>
          <div
            className={classnames(
              sideDrawerClassName ? sideDrawerClassName : styles.drawer,
              {
                [styles.drawerLeft]: !openFromRight,
                [styles.right]: openFromRight,
                [styles.open]: open && !openFromRight,
                [styles.openRight]: open && openFromRight
              }
            )}
          >
            <div className={styles.inner} data-test="SideDrawer__inner">
              {renderContents
                ? renderContents(closeDrawer, openDrawer)
                : children && children}

              <CloseIcon
                wrapperClassName={styles.close}
                onClick={closeDrawer}
              />
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  );
}

export default makeModalContainer(({ ...modalContainerProps }) => (
  <OpenCloseStateContainer
    renderComponent={(open, setOpen, setClosed) => {
      return (
        <SideDrawer
          open={open}
          setOpen={setOpen}
          setClosed={setClosed}
          {...modalContainerProps}
        />
      );
    }}
  />
));
